import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { OrganizationsAPI } from '@makyoapp/makyo-sdk'
import { Order } from '@makyoapp/makyo-sdk/api/api'
import { ListOrganizationType } from '@makyoapp/makyo-sdk/api/Organizations'
import { UsersAPI } from '@makyoapp/makyo-sdk/api/Users'
import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { OrganizationOrderBy } from '@makyoapp/makyo-sdk/models/Organization'
import { fromSdkOrgList, OrgCardAdapter, ProjectAdapter } from '@makyoapp/makyo-common'

import { queryKeys } from '@/lib/react-query'
import { useGetRecentApps } from '@/resolvers/user/query'
import useAuth from '@/hooks/useAuth'
import { EVENT_KEYS } from '@/shared/custom-event'
import { SetterCallback } from '@/shared/types'

type OrgPerms = {
  [k: string]: Record<OrgPermission, boolean>
}

interface OrgsState {
  orgs: OrgCardAdapter[]
  setOrgs: (cb: SetterCallback<OrgCardAdapter[]>) => void
  updateOrgs: () => Promise<OrgCardAdapter[]>
  isOrgsLoading: boolean

  orgsPerms: OrgPerms
  setOrgsPerms: (cb: SetterCallback<OrgPerms>) => void
  updateOrgsPerms: (orgs: OrgCardAdapter[], onSuccess?: () => void, onFinally?: () => void) => Promise<void>
  isOrgsPermsLoading: boolean

  recentApps: ProjectAdapter[]
  updateRecentApps: () => void

  showCreateOrg: boolean
  setShowCreateOrg: (show: boolean) => void
}

/**
 * initial state
 */
const initialState: OrgsState = {
  orgs: [],
  setOrgs: () => {},
  updateOrgs: async () => [],
  isOrgsLoading: false,

  orgsPerms: {},
  setOrgsPerms: () => {},
  updateOrgsPerms: async () => {},
  isOrgsPermsLoading: false,

  recentApps: [],
  updateRecentApps: () => null,

  showCreateOrg: false,
  setShowCreateOrg: () => null,
}

const OrgsContext = createContext<OrgsState>(initialState)

/**
 * use app context hook
 */
export function useOrgsContext() {
  return useContext(OrgsContext)
}

interface OrgsProviderProps {
  children?: React.ReactNode
}

/**
 * AppContext provider
 */
export const OrgsProvider = ({ children }: OrgsProviderProps) => {
  const queryClient = useQueryClient()
  const { userData } = useAuth()
  const userId = userData?.id

  // ============================ State ============================
  const [showCreateOrg, setShowCreateOrg] = useState(false)
  const [recentAppsLocal, setRecentAppsLocal] = useState<ProjectAdapter[]>([])

  // ============================ Orgs ============================
  const orgsQuery = useQuery({
    queryKey: [queryKeys.orgs],
    queryFn: async () => {
      const res = await OrganizationsAPI.ReqList(
        '',
        ListOrganizationType.All,
        Order.Desc,
        OrganizationOrderBy.CreatedAt
      )
      return fromSdkOrgList(res)
    },
    placeholderData: [],
  })

  const orgs = useMemo(() => orgsQuery.data || [], [orgsQuery.data])
  const isOrgsLoading = orgsQuery.isLoading || orgs.length === 0

  const setOrgs = useCallback(
    (cb: SetterCallback<OrgCardAdapter[]>) => {
      queryClient.setQueryData([queryKeys.orgs], cb)
    },
    [queryClient]
  )

  const updateOrgs = useCallback(() => {
    return orgsQuery.refetch().then((res) => {
      res.data?.forEach((o) => {
        if (o.avatar?.startsWith('blob:')) {
          URL.revokeObjectURL(o.avatar)
        }
      })
      return res.data || []
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ============================ Orgs Perms ============================
  const [isOrgsPermsLoading, setIsOrgsPermsLoading] = useState(false)

  const orgsPermsQuery = useQuery({
    queryKey: [queryKeys.orgsPerms],
    queryFn: async () => {
      // return static data instead
      return (queryClient.getQueryData([queryKeys.orgsPerms]) as OrgPerms) ?? {}
    },
    placeholderData: {},
  })

  const orgsPerms = useMemo(() => orgsPermsQuery.data || {}, [orgsPermsQuery.data])

  const setOrgsPerms = useCallback(
    (cb: SetterCallback<OrgPerms>) => {
      queryClient.setQueryData([queryKeys.orgsPerms], cb)
    },
    [queryClient]
  )

  const updateOrgsPerms = useCallback(
    async (orgs: OrgCardAdapter[], onSuccess?: () => void, onFinally?: () => void) => {
      setIsOrgsPermsLoading(true)
      try {
        const promises = orgs.map((org) => {
          return UsersAPI.ReqGetOrgPermissions(org.id).then((res) => {
            setOrgsPerms((prev) => ({
              ...prev,
              [org.id]: res,
            }))
          })
        })
        await Promise.all(promises)
        onSuccess?.()
        //
      } catch (e) {
        //
      } finally {
        onFinally?.()
        setIsOrgsPermsLoading(false)
      }
    },
    [setOrgsPerms]
  )

  // ============================ Recent Apps ============================
  const recentAppsQuery = useGetRecentApps()
  const loadRecentAppsLocal = useCallback(() => {
    if (!userId) {
      setRecentAppsLocal([])
      return
    }
    try {
      const raw = localStorage.getItem(`recentApps:${userId}`)
      const parsed = raw ? JSON.parse(raw) : []
      const list: ProjectAdapter[] = Array.isArray(parsed) ? parsed : []
      setRecentAppsLocal(list)
    } catch (_) {
      setRecentAppsLocal([])
    }
  }, [userId])

  useEffect(() => {
    loadRecentAppsLocal()
    const handler = () => loadRecentAppsLocal()
    window.addEventListener(EVENT_KEYS.RECENT_APPS_UPDATED, handler as EventListener)
    return () => {
      window.removeEventListener(EVENT_KEYS.RECENT_APPS_UPDATED, handler as EventListener)
    }
  }, [loadRecentAppsLocal])

  const recentApps = useMemo(() => {
    const backend = recentAppsQuery.data || []
    return recentAppsLocal.length > 0 ? recentAppsLocal : backend
  }, [recentAppsLocal, recentAppsQuery.data])

  const updateRecentApps = useCallback(async () => {
    await recentAppsQuery.refetch()
    loadRecentAppsLocal()
  }, [recentAppsQuery, loadRecentAppsLocal])

  // ============================ Handlers ============================

  const providerValues = {
    orgs,
    setOrgs,
    updateOrgs,
    isOrgsLoading,

    orgsPerms,
    setOrgsPerms,
    updateOrgsPerms,
    isOrgsPermsLoading,

    recentApps,
    updateRecentApps,

    showCreateOrg,
    setShowCreateOrg,
  }

  return (
    <>
      <OrgsContext.Provider value={providerValues}>{children}</OrgsContext.Provider>
    </>
  )
}
